import React, { useEffect, useRef, useState } from 'react';

import sportsIllustrated from 'assets/images/clients/SportsIllustratedLogo.svg';
import taboola from 'assets/images/clients/Taboola_logo.svg';
import mccann from 'assets/images/clients/mccann-logo-black.svg';
import fansided from 'assets/images/clients/Fansided_logo.svg';
import max from 'assets/images/clients/max.svg';
import meuhedet from 'assets/images/clients/meuhedet.svg';
import ninthmin from 'assets/images/clients/90min-com-logo-vector.svg';
import delta from 'assets/images/clients/logo_delta.svg';
import kan11 from 'assets/images/clients/kan11-black.svg';
import israelBidur from 'assets/images/clients/israel-bidur-black.svg';
import dominos from 'assets/images/clients/DominosPizza.svg';
import nespresso from 'assets/images/clients/nespresso.svg';
import armo from 'assets/images/clients/armo.svg';
import pizzaHut from 'assets/images/clients/pizza-hut-new-vertical.svg';
import eurovision from 'assets/images/clients/eurovision-black.svg';
import ynet from 'assets/images/clients/ynet-black.svg';
import mako from 'assets/images/clients/mako-black.svg';
import n12 from 'assets/images/clients/n12-black.svg';
import walla from 'assets/images/clients/walla-black.svg';
import reshet13 from 'assets/images/clients/reshet13-black.svg';
import israelHayom from 'assets/images/clients/Israel_Hayom.svg';
import sport5 from 'assets/images/clients/sport5-black.svg';
import bigi from 'assets/images/clients/bigl-black.svg';
import catalyst from 'assets/images/clients/catalyst.svg';
import aman from 'assets/images/clients/aman-black.svg';
import styles from './styles.module.scss';

type ClientLogo = {
  src: string;
  id: string;
  name: string;
  scale?: number;
};

const VISIBLE_LOGOS = 6;
const INITIAL_HOLD = 4000;
const SWAP_INTERVAL = 7200;
const TRANSITION_DURATION = 1280;
const COUNT_UP_DURATION = 850;

const metrics = [
  { value: 40, suffix: 'M+', label: 'Monthly unique users' },
  { value: 98, suffix: 'M+', label: 'Audience events tracked' },
  { value: 81, suffix: '%', label: 'Average flow completion rate' },
];

const clients: ClientLogo[] = [
  { src: mccann, id: 'mccann', name: 'McCann', scale: 1.04 },
  { src: taboola, id: 'taboola', name: 'Taboola', scale: 0.98 },
  { src: sportsIllustrated, id: 'sports-illustrated', name: 'Sports Illustrated', scale: 1.14 },
  { src: fansided, id: 'fansided', name: 'FanSided', scale: 1.06 },
  { src: max, id: 'max', name: 'MAX', scale: 0.84 },
  { src: meuhedet, id: 'meuhedet', name: 'Meuhedet', scale: 1.02 },
  { src: ninthmin, id: '90min', name: '90min', scale: 0.92 },
  { src: delta, id: 'delta', name: 'Delta', scale: 0.9 },
  { src: kan11, id: 'kan11', name: 'Kan 11', scale: 0.88 },
  { src: israelBidur, id: 'israel-bidur', name: 'Israel Bidur', scale: 0.92 },
  { src: dominos, id: 'dominos', name: "Domino's", scale: 0.78 },
  { src: nespresso, id: 'nespresso', name: 'Nespresso', scale: 0.95 },
  { src: armo, id: 'armo', name: 'Armo', scale: 0.9 },
  { src: pizzaHut, id: 'pizza-hut', name: 'Pizza Hut', scale: 0.76 },
  { src: eurovision, id: 'eurovision', name: 'Eurovision', scale: 0.94 },
  { src: ynet, id: 'ynet', name: 'Ynet', scale: 0.84 },
  { src: mako, id: 'mako', name: 'Mako', scale: 0.88 },
  { src: n12, id: 'n12', name: 'N12', scale: 0.9 },
  { src: walla, id: 'walla', name: 'Walla', scale: 0.88 },
  { src: reshet13, id: 'reshet13', name: 'Reshet 13', scale: 0.82 },
  { src: israelHayom, id: 'israel-hayom', name: 'Israel Hayom', scale: 0.94 },
  { src: sport5, id: 'sport5', name: 'Sport 5', scale: 0.84 },
  { src: bigi, id: 'bigi', name: 'BIGI', scale: 0.86 },
  { src: catalyst, id: 'catalyst', name: 'Catalyst', scale: 0.9 },
  { src: aman, id: 'aman', name: 'Aman', scale: 0.88 },
];

const clientGroups = clients.map((_, groupIndex) =>
  Array.from(
    { length: VISIBLE_LOGOS },
    (__, slotIndex) => clients[(groupIndex * VISIBLE_LOGOS + slotIndex) % clients.length],
  ),
);

const logoStyle = (client: ClientLogo) =>
  ({ '--logo-scale': client.scale || 1 } as React.CSSProperties);

const ClientsSection = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const proofBarRef = useRef<HTMLDivElement | null>(null);
  const hasCountedUp = useRef(false);
  const [groupIndex, setGroupIndex] = useState(0);
  const [isSwapping, setIsSwapping] = useState(false);
  const [isLogoGridInView, setIsLogoGridInView] = useState(false);
  const [metricValues, setMetricValues] = useState(metrics.map(({ value }) => value));

  const currentGroup = clientGroups[groupIndex];
  const nextGroup = clientGroups[(groupIndex + 1) % clientGroups.length];

  useEffect(() => {
    clients.forEach(({ src }) => {
      const image = new Image();
      image.src = src;
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let initialTimer: number | undefined;
    let interval: number | undefined;
    let settleTimer: number | undefined;
    let observer: IntersectionObserver | undefined;
    let hasStarted = false;

    const swapLogos = () => {
      setIsSwapping(true);
      settleTimer = window.setTimeout(() => {
        setGroupIndex((currentIndex) => (currentIndex + 1) % clientGroups.length);
        setIsSwapping(false);
      }, TRANSITION_DURATION);
    };

    const startRotation = () => {
      if (hasStarted) return;
      hasStarted = true;
      setIsLogoGridInView(true);
      initialTimer = window.setTimeout(() => {
        swapLogos();
        interval = window.setInterval(swapLogos, SWAP_INTERVAL);
      }, INITIAL_HOLD);
    };

    if ('IntersectionObserver' in window && sectionRef.current) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          startRotation();
          observer?.disconnect();
        },
        { threshold: 0.3 },
      );
      observer.observe(sectionRef.current);
    } else {
      startRotation();
    }

    return () => {
      observer?.disconnect();
      if (initialTimer) window.clearTimeout(initialTimer);
      if (interval) window.clearInterval(interval);
      if (settleTimer) window.clearTimeout(settleTimer);
    };
  }, []);

  useEffect(() => {
    const proofBar = proofBarRef.current;
    if (!proofBar || hasCountedUp.current) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      hasCountedUp.current = true;
      return undefined;
    }

    let animationFrame: number | undefined;
    let observer: IntersectionObserver | undefined;

    const startCountUp = () => {
      if (hasCountedUp.current) return;

      hasCountedUp.current = true;
      observer?.disconnect();
      setMetricValues(metrics.map(() => 0));

      let startTime: number | undefined;
      const animate = (time: number) => {
        if (startTime === undefined) startTime = time;

        const progress = Math.min((time - startTime) / COUNT_UP_DURATION, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);

        setMetricValues(metrics.map(({ value }) => Math.round(value * easedProgress)));

        if (progress < 1) {
          animationFrame = window.requestAnimationFrame(animate);
        }
      };

      animationFrame = window.requestAnimationFrame(animate);
    };

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) startCountUp();
        },
        { threshold: 0.35 },
      );
      observer.observe(proofBar);
    } else {
      startCountUp();
    }

    return () => {
      observer?.disconnect();
      if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <section ref={sectionRef} className={styles.clients} aria-labelledby="clients-title">
      <div className={styles.inner}>
        <h2 id="clients-title">Trusted by leading enterprises worldwide.</h2>

        <div
          className={`${styles.logoGrid} ${isLogoGridInView ? styles.inView : ''} ${
            isSwapping ? styles.swapping : ''
          }`}
          aria-label="Storycards customers"
        >
          {currentGroup.map((client, index) => {
            const nextClient = nextGroup[index];
            const delayStyle = { '--slot-delay': `${index * 130}ms` } as React.CSSProperties;

            return (
              <div className={styles.logoSlot} key={`client-slot-${index}`} style={delayStyle}>
                <div className={`${styles.logoLayer} ${styles.currentLogo}`}>
                  <img src={client.src} alt={client.name} style={logoStyle(client)} />
                </div>
                <div className={`${styles.logoLayer} ${styles.nextLogo}`} aria-hidden="true">
                  <img src={nextClient.src} alt="" style={logoStyle(nextClient)} />
                </div>
              </div>
            );
          })}
        </div>

        <div ref={proofBarRef} className={styles.proofBar}>
          <h3>Built to perform at scale.</h3>
          {metrics.map(({ value, suffix, label }, index) => (
            <div className={styles.metric} key={label}>
              <strong aria-label={`${value}${suffix}`}>{`${metricValues[index]}${suffix}`}</strong>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientsSection;
